import type {
  ContentConnectorType, ContentConnectorStatus, ContentPublishingConnector,
  ContentPublishingDispatch,
} from "./types";
import { invokeConnectorManage } from "./_shared";

export type WebhookConnectorConfig = {
  target_url: string;
  auth_header_name?: string | null;
  extra_headers?: Record<string, string> | null;
};

export type EmailConnectorConfig = {
  from_name?: string | null;
  reply_to?: string | null;
  recipients: string[];
  subject_template?: string | null;
};

/** Bento (bentonow.com) connector. The publishable + secret keys are packed
 *  into the encrypted `secret_value` server-side; only `site_uuid` (and
 *  optional from-identity hints) live in the public config. */
export type BentoConnectorConfig = {
  site_uuid: string;
  from_name?: string | null;
  from_email?: string | null;
};

/** WordPress.com connector. Auth is handled by the workspace-level Lovable
 *  connector gateway — no per-connector secret is stored. The user picks
 *  which site to publish to (hostname like `myblog.wordpress.com` or numeric
 *  blog ID). */
export type WordPressConnectorConfig = {
  site: string;
};

/** Self-hosted WordPress connector. Auth = HTTP Basic with an Application
 *  Password generated by the user in wp-admin. The site origin and the
 *  WordPress username live on the public config; the Application Password
 *  is stored encrypted server-side. */
export type WordPressSelfHostedConnectorConfig = {
  site_url: string;
  username: string;
};

/** Ghost (self-hosted or Ghost(Pro)) connector. Auth = a short-lived HS256
 *  JWT minted server-side from the user's Admin API key. The admin URL is
 *  public; the admin key (in `id:secret` format) is stored encrypted. */
export type GhostConnectorConfig = {
  admin_url: string;
};

/** Substack (unofficial) connector. Auth = a session cookie pasted from the
 *  user's signed-in browser. Stored encrypted server-side. Labelled
 *  `unofficial: true` in the registry; all UI must surface that label. */
export type SubstackUnofficialConnectorConfig = {
  publication_url: string;
};

/** Beehiiv connector. Auth = the workspace's Beehiiv V2 API key (stored
 *  encrypted server-side). The publication ID (`pub_xxxxxxxx`) lives on
 *  the public config and identifies which publication to draft into. */
export type BeehiivConnectorConfig = {
  publication_id: string;
};

/** Buttondown connector. Auth = the workspace's Buttondown API key (stored
 *  encrypted server-side). Buttondown is single-newsletter-per-account, so
 *  no extra identifier lives on the public config. */
export type ButtondownConnectorConfig = Record<string, never>;

// Bluesky/Mastodon connector configs removed 2026-07-03.

export type ConnectorUpsertInput = {
  connectorId?: string;
  connectorType: ContentConnectorType;
  displayName: string;
  status?: ContentConnectorStatus;
  config:
    | WebhookConnectorConfig
    | EmailConnectorConfig
    | BentoConnectorConfig
    | WordPressConnectorConfig
    | WordPressSelfHostedConnectorConfig
    | GhostConnectorConfig
    | SubstackUnofficialConnectorConfig
    | BeehiivConnectorConfig
    | ButtondownConnectorConfig;
  /** Pass `null` to clear, `string` to set, `undefined` to leave unchanged.
   *  For Bento connectors this is the *secret key* — pair it with
   *  `publishableKey` so the edge function can pack them together. */
  secretValue?: string | null;
  /** Bento-only: the publishable key half of the Basic-auth pair. */
  publishableKey?: string | null;
};

export const listConnectors = async (workspaceId: string): Promise<ContentPublishingConnector[]> => {
  const data = await invokeConnectorManage(workspaceId, { action: "list" });
  return (data?.connectors ?? []) as ContentPublishingConnector[];
};

export const upsertConnector = async (
  workspaceId: string,
  input: ConnectorUpsertInput,
): Promise<ContentPublishingConnector> => {
  const action = input.connectorId ? "update" : "create";
  const data = await invokeConnectorManage(workspaceId, {
    action,
    connector_id: input.connectorId,
    connector_type: input.connectorType,
    display_name: input.displayName,
    status: input.status ?? "active",
    config: input.config,
    secret_value: input.secretValue === undefined ? undefined : input.secretValue,
    // Bento packs `publishable_key:secret_value` server-side. Sent only when
    // editing/creating a Bento connector; ignored for other types.
    publishable_key: input.publishableKey === undefined ? undefined : input.publishableKey,
  });
  return data.connector as ContentPublishingConnector;
};

export const setConnectorEnabled = async (
  workspaceId: string,
  connectorId: string,
  enabled: boolean,
): Promise<ContentPublishingConnector> => {
  const data = await invokeConnectorManage(workspaceId, {
    action: "disable",
    connector_id: connectorId,
    enable: enabled,
  });
  return data.connector as ContentPublishingConnector;
};

export const deleteConnector = async (workspaceId: string, connectorId: string): Promise<void> => {
  await invokeConnectorManage(workspaceId, { action: "delete", connector_id: connectorId });
};

export const testConnector = async (
  workspaceId: string,
  connectorId: string,
): Promise<{ ok: boolean; connector: ContentPublishingConnector; dispatch: ContentPublishingDispatch }> => {
  const data = await invokeConnectorManage(workspaceId, { action: "test", connector_id: connectorId });
  return {
    ok: !!data.ok,
    connector: data.connector as ContentPublishingConnector,
    dispatch: data.dispatch as ContentPublishingDispatch,
  };
};

export type WordPressSiteOption = {
  id: number;
  name: string;
  url: string;
  is_private: boolean;
};

/** Reports whether the workspace's WordPress.com gateway connection is
 *  linked to the project. Used by the ConnectorManager editor to gate
 *  the WordPress configuration form behind a "Connect WordPress.com" CTA
 *  when the connection is missing. */
export const getWordPressConnectionStatus = async (
  workspaceId: string,
): Promise<{ linked: boolean }> => {
  const data = await invokeConnectorManage(workspaceId, { action: "wordpress_status" });
  return { linked: data?.linked === true };
};

/** Lists the WordPress.com sites the linked account can post to. Powers
 *  the "Load my sites" dropdown in the connector editor. */
export const listWordPressSites = async (
  workspaceId: string,
): Promise<WordPressSiteOption[]> => {
  const data = await invokeConnectorManage(workspaceId, { action: "wordpress_list_sites" });
  const raw = Array.isArray(data?.sites) ? data.sites : [];
  return raw.filter(
    (s: unknown): s is WordPressSiteOption =>
      !!s &&
      typeof s === "object" &&
      typeof (s as WordPressSiteOption).id === "number" &&
      typeof (s as WordPressSiteOption).url === "string",
  );
};
